import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { getLocalizedApiMessage } from "../../../utils/apiMessageHelper";
import {
  createInvoiceRequest,
  deleteInvoiceRequest,
  downloadInvoice,
  getEligibleInvoices,
  getInvoiceCounts,
  getInvoices,
  getPaymentTypes,
  getUserDetails,
  getZatcaTypes,
  updateInvoiceRequest,
} from "../../../services/api/invoices/invoicesApi";

const rejectApiResponse = (responseData, fallbackMessage) => {
  if (responseData?.success === false) {
    return responseData.message ?? fallbackMessage;
  }
  return null;
};

const INVOICE_CREATE_ERROR_FALLBACK = {
  en: "Failed to create invoice",
  ar: "فشل في إنشاء الفاتورة",
};

const INVOICE_UPDATE_ERROR_FALLBACK = {
  en: "Failed to update invoice",
  ar: "فشل في تحديث الفاتورة",
};

const triggerBrowserDownload = (url, fileName) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const getFileNameFromUrl = (url, fallbackName) => {
  if (!url) return fallbackName;

  try {
    const parsedUrl = new URL(url);
    const pathParts = parsedUrl.pathname.split("/").filter(Boolean);
    const lastPart = pathParts[pathParts.length - 1];
    return lastPart || fallbackName;
  } catch (error) {
    const urlParts = url.split("/").filter(Boolean);
    return urlParts[urlParts.length - 1] || fallbackName;
  }
};

export const fetchInvoices = createAsyncThunk(
  "invoices/fetchInvoices",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await getInvoices(params);
      const invoicesBlock = response.data?.data?.invoices;
      const summary = response.data?.data?.summary;

      const invoices = Array.isArray(invoicesBlock?.data)
        ? invoicesBlock.data
        : Array.isArray(invoicesBlock)
          ? invoicesBlock
          : [];

      return {
        invoices,
        summary,
        pagination: {
          current_page: invoicesBlock?.current_page ?? 1,
          last_page: invoicesBlock?.last_page ?? 1,
          total: invoicesBlock?.total ?? invoices.length,
          per_page: invoicesBlock?.per_page ?? params.per_page ?? 15,
          next_page_url: invoicesBlock?.next_page_url ?? null,
          last_page_url: invoicesBlock?.last_page_url ?? null,
        },
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch invoices";
      return rejectWithValue(errorMessage);
    }
  },
);

export const downloadInvoiceFile = createAsyncThunk(
  "invoices/downloadInvoiceFile",
  async (publicId, { rejectWithValue }) => {
    try {
      const response = await downloadInvoice(publicId);
      const downloadUrl = response.data?.download_url;
      if (!downloadUrl) {
        throw new Error("Download URL was not returned by the API.");
      }

      const fallbackName = `invoice_${publicId}.pdf`;
      const fileName = getFileNameFromUrl(downloadUrl, fallbackName);

      triggerBrowserDownload(downloadUrl, fileName);
      toast.success(
        response.data?.message || "Invoice downloaded successfully!",
      );
      return publicId;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to download invoice";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

export const createInvoice = createAsyncThunk(
  "invoices/createInvoice",
  async (invoiceData, { rejectWithValue }) => {
    try {
      const response = await createInvoiceRequest(invoiceData);
      const apiError = rejectApiResponse(
        response.data,
        INVOICE_CREATE_ERROR_FALLBACK,
      );
      if (apiError) {
        return rejectWithValue(apiError);
      }

      return {
        invoice: response.data?.data,
        message: response.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, INVOICE_CREATE_ERROR_FALLBACK),
      );
    }
  },
);

export const deleteInvoice = createAsyncThunk(
  "invoices/deleteInvoice",
  async (invoiceId, { rejectWithValue }) => {
    try {
      const response = await deleteInvoiceRequest(invoiceId);
      toast.success(response.data.message || "Invoice deleted successfully!");
      return invoiceId;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to delete invoice";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

export const updateInvoice = createAsyncThunk(
  "invoices/updateInvoice",
  async ({ invoiceId, invoiceData }, { rejectWithValue }) => {
    try {
      const response = await updateInvoiceRequest(invoiceId, invoiceData);
      const apiError = rejectApiResponse(
        response.data,
        INVOICE_UPDATE_ERROR_FALLBACK,
      );
      if (apiError) {
        return rejectWithValue(apiError);
      }

      return {
        invoice: response.data?.data,
        message: response.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, INVOICE_UPDATE_ERROR_FALLBACK),
      );
    }
  },
);

export const fetchInvoiceCounts = createAsyncThunk(
  "invoices/fetchInvoiceCounts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getInvoiceCounts();
      return response.data.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch invoice counts";
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchPaymentTypes = createAsyncThunk(
  "invoices/fetchPaymentTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getPaymentTypes();
      return response.data.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch payment types";
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchZatcaTypes = createAsyncThunk(
  "invoices/fetchZatcaTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getZatcaTypes();
      return response.data.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch ZATCA types";
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchEligibleInvoices = createAsyncThunk(
  "invoices/fetchEligibleInvoices",
  async (zatca_type_id, { rejectWithValue }) => {
    try {
      const response = await getEligibleInvoices(zatca_type_id);
      return response.data?.invoices ?? response.data?.data?.invoices ?? [];
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch eligible invoices";
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchUserDetails = createAsyncThunk(
  "invoices/fetchUserDetails",
  async (user_id, { rejectWithValue }) => {
    try {
      const response = await getUserDetails(user_id);
      return response.data.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch user details";
      return rejectWithValue(errorMessage);
    }
  },
);

const invoicesSlice = createSlice({
  name: "invoices",
  initialState: {
    invoiceData: [],
    invoiceCounts: {
      total: 0,
      paid: 0,
      unpaid: 0,
      overdue: 0,
      total_paid_amount: 0,
      total_amount: 0,
      total_unpaid_amount: 0,
    },
    paymentTypes: [],
    zatcaTypes: [],
    eligibleInvoices: [],
    currentPage: 1,
    lastPage: 1,
    totalInvoices: 0,
    perPage: 15,
    nextPageUrl: null,
    lastPageUrl: null,
    loading: false,
    error: null,
  },
  reducers: {
    setCurrentPage(state, action) {
      state.currentPage = action.payload;
    },
    setPerPage(state, action) {
      state.perPage = action.payload;
    },
    clearEligibleInvoices: (state) => {
      state.eligibleInvoices = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.invoiceData = Array.isArray(action.payload.invoices)
          ? action.payload.invoices
          : [];
        state.invoiceCounts = action.payload.summary || state.invoiceCounts;
        state.currentPage = action.payload.pagination?.current_page ?? 1;
        state.lastPage = action.payload.pagination?.last_page ?? 1;
        state.totalInvoices = action.payload.pagination?.total ?? 0;
        state.perPage =
          action.payload.pagination?.per_page ?? state.perPage ?? 15;
        state.nextPageUrl = action.payload.pagination?.next_page_url ?? null;
        state.lastPageUrl = action.payload.pagination?.last_page_url ?? null;
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createInvoice.fulfilled, (state, action) => {
        state.loading = false;
        const invoice = action.payload?.invoice;
        if (!invoice) return;

        if (Array.isArray(state.invoiceData)) {
          state.invoiceData.push(invoice);
        } else {
          state.invoiceData = [invoice];
        }
      })
      .addCase(createInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.invoiceData = state.invoiceData.filter(
          (invoice) => invoice.id !== action.payload,
        );
        const lang =
          window.localStorage.getItem("language") === "ar" ? "ar" : "en";
        const msg =
          lang === "ar"
            ? "تم حذف الفاتورة بنجاح!"
            : "Invoice deleted successfully!";
        toast.success(msg);
      })
      .addCase(deleteInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        const lang =
          window.localStorage.getItem("language") === "ar" ? "ar" : "en";
        let msg = action.payload;
        if (lang === "ar") {
          msg =
            typeof msg === "string" && msg.includes("Failed")
              ? "فشل في حذف الفاتورة"
              : msg;
        }
        toast.error(
          msg ||
            (lang === "ar"
              ? "فشل في حذف الفاتورة"
              : "Failed to delete invoice"),
        );
      })
      .addCase(updateInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInvoice.fulfilled, (state, action) => {
        state.loading = false;
        const invoice = action.payload?.invoice;
        if (!invoice) return;

        const index = state.invoiceData.findIndex(
          (item) => item.id === invoice.id,
        );
        if (index !== -1) {
          state.invoiceData[index] = invoice;
        }
      })
      .addCase(updateInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchInvoiceCounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoiceCounts.fulfilled, (state, action) => {
        state.loading = false;
        state.invoiceCounts = action.payload;
      })
      .addCase(fetchInvoiceCounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchPaymentTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentTypes = action.payload;
      })
      .addCase(fetchPaymentTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchZatcaTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchZatcaTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.zatcaTypes = action.payload;
      })
      .addCase(fetchZatcaTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchEligibleInvoices.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchEligibleInvoices.fulfilled, (state, action) => {
        state.eligibleInvoices = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchEligibleInvoices.rejected, (state, action) => {
        state.eligibleInvoices = [];
        state.error = action.payload;
      });
  },
});

export const { clearEligibleInvoices, setCurrentPage, setPerPage } =
  invoicesSlice.actions;
export default invoicesSlice.reducer;
